/**
 * window.MBLiveChat - the single, provider-agnostic entry point every Live
 * Chat CTA on the site calls: window.MBLiveChat.open(event).
 */
(function () {
  'use strict';

  var CONFIG = window.MB_LIVE_CHAT_CONFIG || {};
  var LOG_PREFIX = '[MBLiveChat]';

  var PROVIDERS = { ZENDESK: 'zendesk', MAVEN: 'maven' };

  var DEV_OVERRIDE_PARAM = 'mb_live_chat';
  var DEV_OVERRIDE_CLEAR = 'default';
  var DEV_OVERRIDE_KEY = 'mbLiveChatProviderOverride';

  var ZENDESK_SRC =
    'https://static.zdassets.com/ekr/snippet.js?key=19a34ff9-88df-409a-9d81-5ca4154caad5';

  var MOBILE_QUERY = '(max-width: 749px)';
  var MAVEN_HOST = 'onmaven.app';
  var MAVEN_HOST_SUFFIX = '.onmaven.app';

  var ZIP_PATTERN = /^\d{5}(-\d{4})?$/;
  var EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  var USER_CORRECTABLE_STATUSES = [400, 401, 403, 404, 422];

  function warn(message) {
    if (window.console && window.console.warn) {
      window.console.warn(LOG_PREFIX + ' ' + message);
    }
  }

  function emit(name, detail) {
    try {
      document.dispatchEvent(new CustomEvent(name, { detail: detail, bubbles: true }));
    } catch (e) {
      // CustomEvent unavailable on very old browsers; events are optional.
    }
  }

  /* ======================================================================
   * Provider resolution
   * ====================================================================== */

  function readDevOverride() {
    try {
      var params = new URLSearchParams(window.location.search);
      var fromParam = params.get(DEV_OVERRIDE_PARAM);

      if (fromParam === PROVIDERS.MAVEN || fromParam === PROVIDERS.ZENDESK) {
        window.sessionStorage.setItem(DEV_OVERRIDE_KEY, fromParam);
        warn(
          'Dev override active for this tab: ' +
            fromParam +
            '. Clear with sessionStorage.removeItem("' +
            DEV_OVERRIDE_KEY +
            '") or close the tab.'
        );
        return fromParam;
      }

      if (fromParam === DEV_OVERRIDE_CLEAR) {
        window.sessionStorage.removeItem(DEV_OVERRIDE_KEY);
        return null;
      }

      var stored = window.sessionStorage.getItem(DEV_OVERRIDE_KEY);
      if (stored === PROVIDERS.MAVEN || stored === PROVIDERS.ZENDESK) return stored;
    } catch (e) {
      // sessionStorage/URLSearchParams unavailable - no override, safe default.
    }
    return null;
  }

  /**
   * Reads the generic provider signal an experiment's variation JS is
   * expected to set (see PROVIDER SIGNAL above). Only 'maven' and 'zendesk'
   * are valid; anything else - unset, a typo, a non-string - is treated as
   * "no signal" so the default takes over.
   */
  function readProviderSignal() {
    var signal = window.MB_LIVE_CHAT_PROVIDER;
    if (signal === PROVIDERS.MAVEN || signal === PROVIDERS.ZENDESK) return signal;
    return null;
  }

  /**
   * True only on stores snippets/mb-live-chat-integration.liquid recognised
   * by shop.permanent_domain. Any other store gets no Maven config, so it can
   * never call a Maven endpoint.
   */
  function mavenConfigured() {
    return !!(CONFIG.organizationId && CONFIG.agentId && CONFIG.signedUserDataPath);
  }

  function resolveProvider() {
    var provider = readDevOverride() || readProviderSignal() || PROVIDERS.ZENDESK;

    if (provider === PROVIDERS.MAVEN && !mavenConfigured()) {
      warn('Maven is not configured for this store; using Zendesk.');
      return PROVIDERS.ZENDESK;
    }

    return provider;
  }

  /* ======================================================================
   * Zendesk provider
   * ====================================================================== */

  var zendeskScriptPromise = null;

  function zendeskWidget() {
    return window.zE || null;
  }

  function loadZendeskScript() {
    if (zendeskScriptPromise) return zendeskScriptPromise;

    if (zendeskWidget()) {
      zendeskScriptPromise = Promise.resolve();
      return zendeskScriptPromise;
    }

    var existing = document.getElementById('ze-snippet');

    zendeskScriptPromise = new Promise(function (resolve, reject) {
      if (existing) {
        existing.addEventListener('load', resolve, { once: true });
        existing.addEventListener(
          'error',
          function () {
            zendeskScriptPromise = null;
            reject(new Error('Zendesk snippet.js failed to load'));
          },
          { once: true }
        );
        return;
      }

      var script = document.createElement('script');
      script.id = 'ze-snippet';
      script.src = ZENDESK_SRC;
      script.async = true;
      script.onload = function () {
        resolve();
      };
      script.onerror = function () {
        zendeskScriptPromise = null;
        reject(new Error('Zendesk snippet.js failed to load'));
      };
      document.head.appendChild(script);
    });

    return zendeskScriptPromise;
  }

  function openZendesk() {
    return loadZendeskScript()
      .then(function () {
        if (zendeskWidget()) window.zE('webWidget', 'open');
        emit('mb:live-chat:open', { provider: PROVIDERS.ZENDESK });
      })
      .catch(function (error) {
        warn(error && error.message ? error.message : 'Zendesk failed to open');
        emit('mb:live-chat:error', {
          provider: PROVIDERS.ZENDESK,
          message: error && error.message,
        });
      });
  }

  /* ======================================================================
   * UserWay coordination (Maven only, mobile only)
   * ====================================================================== */

  var mavenChatOpen = false;
  var mavenPrechatOpen = false;
  var userWayHiddenByUs = false;

  
  var DEBUG = !!(CONFIG.debug || window.MB_LIVE_CHAT_DEBUG);
  try {
    if (new URLSearchParams(window.location.search).get('mb_live_chat_debug') === '1') {
      DEBUG = true;
    }
  } catch (e) {
    // URLSearchParams unavailable - DEBUG stays as already set above.
  }

  function debugLog(message) {
    if (!DEBUG) return;
    if (window.console && window.console.log) {
      window.console.log(LOG_PREFIX + ' [userway-debug] ' + message);
    }
  }

  function isMobile() {
    return window.matchMedia ? window.matchMedia(MOBILE_QUERY).matches : false;
  }

  function userWayInstance() {
    if (window.UserWay && typeof window.UserWay.iconVisibilityOff === 'function') {
      return window.UserWay;
    }
    return null;
  }

  function syncUserWay() {
    var mobile = isMobile();
    var shouldHide = (mavenChatOpen || mavenPrechatOpen) && mobile;
    var uw = userWayInstance();

    debugLog(
      'sync mobile=' + mobile +
        ' prechatOpen=' + mavenPrechatOpen +
        ' chatOpen=' + mavenChatOpen +
        ' hiddenByUs=' + userWayHiddenByUs +
        ' userWayApiAvailable=' + !!uw
    );

    if (shouldHide) {
      if (userWayHiddenByUs || !uw) return;
      try {
        uw.iconVisibilityOff();
        userWayHiddenByUs = true;
        debugLog('OFF called');
      } catch (e) {
        // UserWay API unavailable/changed; leave the launcher alone.
      }
      return;
    }

    // Only ever restore a launcher this file hid, so a launcher UserWay or
    // another integration hid deliberately can never be revealed here.
    if (!userWayHiddenByUs) return;
    if (!uw || typeof uw.iconVisibilityOn !== 'function') return;

    try {
      uw.iconVisibilityOn();
      debugLog('ON called');
    } catch (e) {
      // See above.
    }
    userWayHiddenByUs = false;
  }

  function setMavenChatOpen(open) {
    mavenChatOpen = open;
    syncUserWay();
  }

  function setMavenPrechatOpen(open) {
    mavenPrechatOpen = open;
    syncUserWay();
  }

  var mobileQuery = window.matchMedia ? window.matchMedia(MOBILE_QUERY) : null;
  if (mobileQuery && typeof mobileQuery.addEventListener === 'function') {
    // Leaving the mobile breakpoint mid-session restores a hidden launcher.
    mobileQuery.addEventListener('change', syncUserWay);
  }

  /*
   * UserWay's widget.js is loaded with `defer` from a third-party CDN
   */
  document.addEventListener('userway:init_completed', function () {
    debugLog('userway:init_completed received');
    syncUserWay();
  });

  /** Accepts only messages that really came from the Maven chat frame. */
  function isFromMavenFrame(event) {
    var host;
    try {
      host = new URL(event.origin).hostname;
    } catch (e) {
      return false;
    }

    if (host !== MAVEN_HOST && host.slice(-MAVEN_HOST_SUFFIX.length) !== MAVEN_HOST_SUFFIX) {
      return false;
    }

    var frame = document.querySelector('#maven-chat-widget iframe');
    if (frame && frame.contentWindow && event.source) {
      return frame.contentWindow === event.source;
    }

    return true;
  }

  /*
   * MAVEN_CLOSE_WIDGET is what Maven's iframe posts when the visitor closes
   * chat from inside it (e.g. the X in the chat header) - a close this file
   * would otherwise never see, since it never goes through MBLiveChat.open().
   */
  window.addEventListener('message', function (event) {
    var data = event && event.data;
    if (!data || typeof data !== 'object' || data.type !== 'MAVEN_CLOSE_WIDGET') return;
    if (!isFromMavenFrame(event)) return;

    setMavenChatOpen(false);
  });

  /* ======================================================================
   * Maven provider
   * ====================================================================== */

  /**
   * Lazily loads assets/mb-maven-widget.js and resolves to its public API
   */
  var mavenModulePromise = null;
  function mavenWidgetModule() {
    if (mavenModulePromise) return mavenModulePromise;

    if (window.MBMavenWidget) {
      mavenModulePromise = Promise.resolve(window.MBMavenWidget);
      return mavenModulePromise;
    }

    mavenModulePromise = new Promise(function (resolve, reject) {
      var script = document.createElement('script');
      script.type = 'module';
      script.src = CONFIG.mavenWidgetModuleUrl;
      script.onload = function () {
        if (window.MBMavenWidget) {
          resolve(window.MBMavenWidget);
        } else {
          reject(new Error('mb-maven-widget.js loaded but window.MBMavenWidget is missing'));
        }
      };
      script.onerror = function () {
        reject(new Error('mb-maven-widget.js failed to load'));
      };
      document.head.appendChild(script);
    }).catch(function (error) {
      mavenModulePromise = null; // allow a later click to retry
      throw error;
    });

    return mavenModulePromise;
  }

  var tokenPromise = null;

  /**
   * Logged-in visitors only. Identified by the Shopify session; carries no
   * identity of its own. Manually verified against staging - do not change
   * the request shape.
   */
  function getSignedUserData() {
    if (tokenPromise) return tokenPromise;

    tokenPromise = fetch(CONFIG.signedUserDataPath, {
      headers: {
        'X-Organization-Id': CONFIG.organizationId,
        'X-Agent-Id': CONFIG.agentId,
      },
      credentials: 'same-origin',
    })
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Unable to retrieve signed user data: ' + response.status);
        }
        return response.json();
      })
      .then(function (data) {
        if (!data || !data.token) {
          throw new Error('Signed user data response did not include a token');
        }
        return data.token;
      })
      .catch(function (error) {
        tokenPromise = null; // allow a later click to retry
        throw error;
      });

    return tokenPromise;
  }

  /**
   * Verifies a logged-out visitor by email + billing ZIP and resolves with
   */
  function warnPreChatDiagnostics(email, billingZip, responseStatus) {
    var payload = { email: email, billing_zip: billingZip };
    warn(
      'Pre-chat diagnostics - ' +
        'typeof email: ' +
        typeof email +
        ', email length: ' +
        (typeof email === 'string' ? email.length : 'n/a') +
        ', email contains @: ' +
        (typeof email === 'string' && email.indexOf('@') !== -1) +
        ', typeof billing_zip: ' +
        typeof billingZip +
        ', billing_zip length: ' +
        (typeof billingZip === 'string' ? billingZip.length : 'n/a') +
        ', payload keys: ' +
        Object.keys(payload).join(',') +
        (responseStatus ? ', response status: ' + responseStatus : '')
    );
  }

  function verifyPreChatCustomer(email, billingZip) {
    return fetch(CONFIG.signedUserDataPath, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Organization-Id': CONFIG.organizationId,
        'X-Agent-Id': CONFIG.agentId,
      },
      credentials: 'same-origin',
      body: JSON.stringify({ email: email, billing_zip: billingZip }),
    })
      .then(function (response) {
        if (!response.ok) {
          warnPreChatDiagnostics(email, billingZip, response.status);
          var failed = new Error('Pre-chat verification failed: ' + response.status);
          failed.messageKey =
            USER_CORRECTABLE_STATUSES.indexOf(response.status) !== -1 ? 'nomatch' : 'generic';
          throw failed;
        }
        return response.json();
      })
      .then(function (data) {
        if (!data || !data.token) {
          warnPreChatDiagnostics(email, billingZip, null);
          var missing = new Error('Pre-chat verification failed: response contained no token');
          missing.messageKey = 'generic';
          throw missing;
        }
        return data.token;
      });
  }

  var loadInvoked = false; // Maven.ChatWidget.load() has been reached - never call it twice
  var loadSucceeded = false;
  var loadPromise = null;

  /**
   * Loads the Maven widget from a token source. Shared by both entry points
   * so the single-flight guard and the never-load-without-a-token rule hold
   * no matter which one runs first.
   */
  function initMaven(getToken) {
    if (loadPromise) return loadPromise;

    loadPromise = Promise.all([mavenWidgetModule(), getToken()])
      .then(function (results) {
        var mod = results[0];
        var token = results[1];

        if (!token) {
          throw new Error('Refusing to load chat without signed user data');
        }
        if (loadInvoked) return mod;

        loadInvoked = true;
        return mod
          .load({
            widgetSrc: CONFIG.mavenWidgetSrc,
            organizationId: CONFIG.organizationId,
            agentId: CONFIG.agentId,
            signedUserData: token,
          })
          .then(function () {
            return mod;
          });
      })
      .then(function (mod) {
        loadSucceeded = true;
        return mod;
      })
      .catch(function (error) {
        if (!loadInvoked) loadPromise = null;
        throw error;
      });

    return loadPromise;
  }

  function openLoadedMaven(mod, source) {
    if (!loadSucceeded) return false;
    if (typeof mod.isOpen === 'function' && mod.isOpen()) return true;
    mod.open();
    setMavenChatOpen(true);
    emit('mb:live-chat:open', { provider: PROVIDERS.MAVEN, source: source });
    return true;
  }

  function openMavenLoggedIn() {
    return initMaven(getSignedUserData)
      .then(function (mod) {
        openLoadedMaven(mod, 'signed-in');
      })
      .catch(function (error) {
        var message = error && error.message ? error.message : 'unknown error';
        warn(message);
        setMavenChatOpen(false);
        emit('mb:live-chat:error', { provider: PROVIDERS.MAVEN, message: message });
      });
  }

  /* ----------------------------------------------------------------------
   * Pre-chat verification modal (logged-out visitors)
   * -------------------------------------------------------------------- */

  var preChatInFlight = false;

  function prechatModal() {
    return document.getElementById(CONFIG.prechatModalId);
  }

  function prechatParts() {
    var modal = prechatModal();
    if (!modal) return null;

    var form = modal.querySelector('[data-mb-maven-prechat-form]');
    if (!form) return null;

    return {
      modal: modal,
      form: form,
      email: form.querySelector('[data-mb-maven-prechat-email]'),
      zip: form.querySelector('[data-mb-maven-prechat-zip]'),
      submit: form.querySelector('[data-mb-maven-prechat-submit]'),
      error: form.querySelector('[data-mb-maven-prechat-error]'),
      label: form.querySelector('[data-mb-maven-prechat-label]'),
      busy: form.querySelector('[data-mb-maven-prechat-busy]'),
    };
  }

  function fieldsAreValid(parts) {
    if (!parts.email || !parts.zip) return false;

    var email = parts.email.value.trim();
    var zip = parts.zip.value.trim();

    var emailOk =
      !!email &&
      EMAIL_PATTERN.test(email) &&
      (typeof parts.email.checkValidity !== 'function' || parts.email.checkValidity());

    return emailOk && ZIP_PATTERN.test(zip);
  }

  function showPreChatError(parts, message) {
    if (!parts.error) return;
    parts.error.textContent = message;
    parts.error.hidden = false;
  }

  function clearPreChatError(parts) {
    if (!parts.error) return;
    parts.error.hidden = true;
    parts.error.textContent = '';
  }

  function syncPreChatSubmit(parts) {
    if (!parts.submit) return;
    parts.submit.disabled = preChatInFlight || !fieldsAreValid(parts);
  }

  function setPreChatBusy(parts, busy) {
    preChatInFlight = busy;

    if (parts.email) parts.email.disabled = busy;
    if (parts.zip) parts.zip.disabled = busy;
    if (parts.label) parts.label.hidden = busy;
    if (parts.busy) parts.busy.hidden = !busy;

    if (parts.submit) {
      if (busy) parts.submit.setAttribute('aria-busy', 'true');
      else parts.submit.removeAttribute('aria-busy');
    }

    syncPreChatSubmit(parts);
  }

  function preChatMessage(parts, key) {
    var fallback =
      'We could not verify your account right now. Please check your details and try again.';
    if (!parts.error) return fallback;
    return parts.error.getAttribute('data-message-' + key) || fallback;
  }

  function openPreChatGate() {
    var modal = prechatModal();
    if (!modal || typeof modal.showDialog !== 'function') {
      warn('Pre-chat verification modal is unavailable; chat was not opened.');
      emit('mb:live-chat:error', { provider: PROVIDERS.MAVEN, message: 'prechat-modal-unavailable' });
      return;
    }

    var parts = prechatParts();
    if (parts) {
      clearPreChatError(parts);
      setPreChatBusy(parts, false);
    }

    modal.showDialog();
    emit('mb:live-chat:prechat', { provider: PROVIDERS.MAVEN });
  }

  /**
   * Runs the verified hand-off. load() is awaited BEFORE the modal closes,
   * so a failure at any stage leaves the modal on screen with an inline
   * error to retry from rather than dismissing it over a chat window that
   * never appeared.
   */
  function submitPreChat(parts) {
    clearPreChatError(parts);
    setPreChatBusy(parts, true);

    verifyPreChatCustomer(parts.email.value.trim(), parts.zip.value.trim())
      .then(function (token) {
        return initMaven(function () {
          return Promise.resolve(token);
        });
      })
      .then(function (mod) {
        if (!loadSucceeded) {
          throw new Error('Chat did not finish initialising');
        }

        setPreChatBusy(parts, false);
        if (typeof parts.modal.closeDialog === 'function') parts.modal.closeDialog();
        openLoadedMaven(mod, 'prechat');
      })
      .catch(function (error) {
        // Never fall back to anonymous chat: the modal stays open, the
        // visitor sees why, and the form is live again for a retry.
        setPreChatBusy(parts, false);
        warn(error && error.message ? error.message : 'Pre-chat verification failed');
        showPreChatError(parts, preChatMessage(parts, (error && error.messageKey) || 'generic'));
        emit('mb:live-chat:error', {
          provider: PROVIDERS.MAVEN,
          message: error && error.message ? error.message : 'prechat-verification-failed',
        });
      });
  }

  function openMaven() {
    if (!mavenConfigured()) {
      warn('Maven is not configured; chat was not opened.');
      emit('mb:live-chat:error', { provider: PROVIDERS.MAVEN, message: 'not-configured' });
      return Promise.resolve();
    }

    if (!CONFIG.isLoggedIn) {
      openPreChatGate();
      return Promise.resolve();
    }

    return openMavenLoggedIn();
  }

  /*
   * Tracks the pre-chat modal's own open/close events (dialog.js's
   * DialogOpenEvent/DialogCloseEvent, dispatched on the dialog-component
   * element) so UserWay reacts no matter how the modal closes - the X
   * button, Escape, an outside click, or this file's own closeDialog() call
   * on a successful hand-off to Maven.
   */
  document.addEventListener('dialog:open', function (event) {
    if (event.target && event.target.id === CONFIG.prechatModalId) {
      setMavenPrechatOpen(true);
    }
  });
  document.addEventListener('dialog:close', function (event) {
    if (event.target && event.target.id === CONFIG.prechatModalId) {
      setMavenPrechatOpen(false);
    }
  });

  document.addEventListener('input', handlePreChatFieldChange);
  document.addEventListener('change', handlePreChatFieldChange);

  function handlePreChatFieldChange(event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;
    if (!target.closest('[data-mb-maven-prechat-form]')) return;

    var parts = prechatParts();
    if (!parts) return;

    clearPreChatError(parts);
    syncPreChatSubmit(parts);
  }

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || typeof form.closest !== 'function') return;
    if (!form.closest('[data-mb-maven-prechat-form]')) return;

    event.preventDefault(); // the modal must never navigate the page

    var parts = prechatParts();
    if (!parts || !parts.email || !parts.zip) return;

    // Duplicate-submission guard: Enter-key repeats and double taps land
    // here while the first request is still outstanding.
    if (preChatInFlight) return;

    if (!fieldsAreValid(parts)) {
      // Client-side rejection - the request is never sent. Same safe-metadata
      // diagnostic as the network path (see warnPreChatDiagnostics), so a
      // "valid input rejected" repro can tell the two apart.
      warnPreChatDiagnostics(parts.email.value.trim(), parts.zip.value.trim(), null);
      showPreChatError(parts, preChatMessage(parts, 'invalid'));
      syncPreChatSubmit(parts);
      return;
    }

    submitPreChat(parts);
  });

  /*
   * Sync the submit button once at startup. The markup ships `disabled`
   * (correct for two empty fields, and the safe state with JS off), but a
   * restored or autofilled form can already be valid before the first
   * keystroke.
   */
  function syncPreChatOnReady() {
    var parts = prechatParts();
    if (parts) syncPreChatSubmit(parts);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncPreChatOnReady);
  } else {
    syncPreChatOnReady();
  }

  /* ======================================================================
   * Public, provider-neutral bridge
   * ====================================================================== */

  var openInFlight = false;

  function open(event) {
    if (event && typeof event.preventDefault === 'function') event.preventDefault();
    if (openInFlight) return;

    openInFlight = true;
    var release = function () {
      openInFlight = false;
    };

    var provider = resolveProvider();
    var result = provider === PROVIDERS.MAVEN ? openMaven() : openZendesk();
    Promise.resolve(result).then(release, release);
  }

  window.MBLiveChat = {
    open: open,
    resolveProvider: resolveProvider,
    PROVIDERS: PROVIDERS,
  };
})();
